import React, { useState } from "react";
import {
  Layout,
  Card,
  TextContainer,
  TextField,
  TextStyle,
  FormLayout,
  Collapsible,
  Link,
  Banner,
  Button,
} from "@shopify/polaris";
import { Flex, Box } from "rebass";
import Toggle from "react-toggle";
import { useToast } from "providers/ToastProvider";
import SubscriptionGateway from "components/auth/SubscriptionGateway";
import { sendTestWebhook } from "services/wpApi";

export default function WebhookSettings({ values, setFieldValue, errors }) {
  const [docsOpen, setDocsOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const setToast = useToast();

  const handleSendTestWebhook = async () => {
    setTesting(true);
    try {
      await sendTestWebhook({
        webhookUrl: values.webhookUrl,
        webhookSecret: values.webhookSecret,
      });
      setToast("Webhook sent");
    } catch (err) {
      setToast(
        err.response?.data || "Unable to send the test webhook. Please try again."
      );
    } finally {
      setTesting(false);
    }
  };

  return (
    <Layout.AnnotatedSection
      title="Webhooks"
      description="Real ID can notify your application or workflow when ID checks are completed, in real time. The URL should return a 200 status code in the response, otherwise Real ID will attempt to resend the request with expotential back off, for a maximum of 6 retries."
    >
      <Card title="ID check events">
        <Card.Section>
          <TextContainer spacing="loose">
            <p>
              <TextStyle variation="subdued">
                Real ID will send a webhook to this URL on ID check events. You
                can automate actions from this event such as notifying your
                fulfillment system or releasing held orders.
              </TextStyle>
            </p>
            <p>
              <TextStyle variation="subdued">
                Prefer to run PHP directly? Use our{" "}
                <Link
                  external
                  url="https://getverdict.com/help/docs/woocommerce/hooks"
                >
                  WooCommerce ID check event hooks
                </Link>{" "}
                to react to verification events without standing up a webhook
                endpoint.
              </TextStyle>
            </p>
            <p>
              <TextStyle variation="subdued">
                <Link onClick={() => setDocsOpen(!docsOpen)}>
                  Example event payload
                </Link>
              </TextStyle>
            </p>
          </TextContainer>
          <Collapsible open={docsOpen}>
            <TextContainer>
              <Box
                sx={`
                pre {
                  background-color: #dfe3e8;
                }

                pre code {
                  display: block;
                  background: none;
                  white-space: pre;
                  -webkit-overflow-scrolling: touch;
                  overflow-x: scroll;
                  max-width: 100%;
                  min-width: 100px;
                  padding: 0;
                }

              `}
              >
                <pre>
                  <code>
                    {`
                    {
                      "event": "check.completed"
                      "check": {
                        "id": "abcedf",
                        "step": "completed",
                        "testing": false
                      }
                    }
                  `}
                  </code>
                </pre>
                <Banner status="info">
                  <p>
                    For more information about available statuses and retrieving
                    the check details, please{" "}
                    <Link external url="https://docs.getverdict.com">
                      visit our API documentation
                    </Link>
                    .
                  </p>
                </Banner>
              </Box>
            </TextContainer>
          </Collapsible>
          <SubscriptionGateway permission="branding" minimumPlan="Pro">
            <Flex justifyContent="flex-start" alignItems="center">
              <Box my={4} mx={3} display="flex" alignItems="center">
                <Toggle
                  checked={values.webhookEnabled}
                  onChange={(e) => {
                    setFieldValue("webhookEnabled", e.target.checked);
                  }}
                />
              </Box>
              <Box>
                Webhooks on ID check events are currently{" "}
                <strong>
                  {values.webhookEnabled ? "enabled" : "disabled"}
                </strong>
              </Box>
            </Flex>
            <FormLayout>
              <TextField
                placeholder={"https://example.com/api/checks/completed"}
                value={values.webhookUrl}
                label="Webhook URL"
                inputMode="url"
                type="url"
                onChange={(value) => setFieldValue("webhookUrl", value)}
                error={errors.webhookUrl}
              />
              <TextField
                disabled={true}
                label="Signing secret"
                value={values.webhookSecret}
                helpText={
                  <>
                    Validate the webhook by ensuring the header{" "}
                    <TextStyle variation="code">X-Real-ID-Signature</TextStyle>{" "}
                    in the request matches the body of the request signed with
                    this key.
                  </>
                }
              />
              <Button
                loading={testing}
                disabled={!values.webhookUrl || errors.webhookUrl || testing}
                onClick={handleSendTestWebhook}
              >
                Send test webhook
              </Button>
            </FormLayout>
          </SubscriptionGateway>
        </Card.Section>
      </Card>
    </Layout.AnnotatedSection>
  );
}
